import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import { ArrowLeft } from 'lucide-react';

interface Customer {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  company?: string;
  contactPerson?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ProjectForm {
  name: string;
  description?: string;
  customerName: string;
  startDate: string;
  endDate?: string;
  status: 'planning' | 'active' | 'on-hold' | 'completed' | 'cancelled';
  actualCost: number;
  workOrders?: { number: string; date: string }[];
  projectAddress?: string;
  notes?: string;
}

const ProjectEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<ProjectForm | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newWorkOrder, setNewWorkOrder] = useState({ number: '', date: '' });
  const [modalOpen, setModalOpen] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [projectRes, customersRes] = await Promise.all([
          apiService.get(`/projects/${id}`),
          apiService.get('/customers'),
        ]);
        const p = projectRes.data;
        setForm({
          name: p.name || '',
          description: p.description || '',
          customerName: p.customerName || '',
          startDate: p.startDate ? p.startDate.slice(0, 10) : '',
          endDate: p.endDate ? p.endDate.slice(0, 10) : '',
          status: p.status || 'planning',
          actualCost: p.actualCost || 0,
          workOrders: p.workOrders || [],
          projectAddress: p.projectAddress || '',
          notes: p.notes || '',
        });
        setCustomers(customersRes.data.customers || []);
      } catch (e) {
        setError('Failed to load project or customers.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => prev ? { ...prev, [name]: value } : prev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError('');
    try {
      await apiService.put(`/projects/${id}`, {
        ...form,
        actualCost: Number(form.actualCost),
        workOrders: form.workOrders ?? [], // ensure workOrders is always sent
      });
      navigate(`/projects`);
    } catch (e) {
      setError('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>;
  }

  return (
    <Modal
      isOpen={modalOpen}
      onClose={() => { setModalOpen(false); navigate(-1); }}
      title="Edit Project"
      size="lg"
      showCloseButton
      className="z-[10000]"
    >
      <div className="max-w-2xl mx-auto py-2 px-2 font-['Times_New_Roman']">
      <button
          onClick={() => { setModalOpen(false); navigate(-1); }}
          className="flex items-center px-3 py-2 mb-4 bg-[var(--color-surface-light)] text-[var(--color-text)] rounded-lg shadow hover:bg-[var(--color-surface)] transition-colors"
        title="Back"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back
      </button>
        {error && <div className="text-[var(--color-danger)] mb-4">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Project Name" name="name" value={form.name} onChange={handleChange} required />
        <div>
            <label className="block mb-1 font-medium text-[var(--color-text-secondary)]">Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
            rows={3}
          />
        </div>
        <div>
            <label className="block mb-1 font-medium text-[var(--color-text-secondary)]">Customer</label>
          <select
            name="customerName"
            value={form.customerName}
            onChange={handleChange}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
            required
          >
            <option value="">Select Customer</option>
            {customers.map((c) => (
              <option key={c._id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>
        <Input label="Start Date" name="startDate" type="date" value={form.startDate} onChange={handleChange} required />
        <Input label="End Date" name="endDate" type="date" value={form.endDate || ''} onChange={handleChange} />
        <div>
            <label className="block mb-1 font-medium text-[var(--color-text-secondary)]">Status</label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
            required
          >
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="on-hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
            <label className="block mb-1 font-medium text-[var(--color-text-secondary)]">Work Orders</label>
          {form.workOrders && form.workOrders.length > 0 && (
            <ul className="mb-2">
              {form.workOrders.map((wo, idx) => (
                  <li key={idx} className="flex items-center space-x-2 mb-1 text-[var(--color-text)]">
                  <span>{wo.number} ({wo.date ? new Date(wo.date).toLocaleDateString() : ''})</span>
                    <button type="button" onClick={() => setForm(f => f ? { ...f, workOrders: (f.workOrders ?? []).filter((_, i) => i !== idx) } : f)} className="text-[var(--color-danger)]">Remove</button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex space-x-2">
            <Input label="Number" value={newWorkOrder.number} onChange={e => setNewWorkOrder({ ...newWorkOrder, number: e.target.value })} />
            <Input label="Date" type="date" value={newWorkOrder.date} onChange={e => setNewWorkOrder({ ...newWorkOrder, date: e.target.value })} />
            <button type="button" onClick={() => {
              if (newWorkOrder.number) {
                setForm(f => f ? { ...f, workOrders: [...(f.workOrders ?? []), newWorkOrder] } : f);
                setNewWorkOrder({ number: '', date: '' });
              }
              }} className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']">Add</button>
          </div>
        </div>
        <Input label="Project Address" name="projectAddress" value={form.projectAddress || ''} onChange={handleChange} />
        <Input label="Actual Cost" name="actualCost" type="number" value={form.actualCost} onChange={handleChange} min={0} />
        <div>
            <label className="block mb-1 font-medium text-[var(--color-text-secondary)]">Notes</label>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
            rows={2}
          />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
        </div>
      </form>
    </div>
    </Modal>
  );
};

export default ProjectEditPage; 